module Client.Client

open FSharpPlus
open Elmish
open Fable.React
open Fable.React.Props
open Elmish.Bridge
open PortalLibs.Client.Auth
open Shared
open Fulma
open PortalLibs.Client.Util
open Thoth.Elmish
open Pages
open Fable.FontAwesome
open PortalLibs.Client
open Branding
open PortalLibs.Shared.NewTable
open PortalLibs.Shared
open Model
open ClientModel
open Elmish.Navigation
open AI
open System
open Feliz
open Client.ContractPage

let restoreScrollCmd scroll page =
  Cmd.OfFunc.perform
    scrollTo
    (scroll
     |> Map.tryFind page
     |> Option.defaultValue 0.0)
    (fun _ -> NoOp)

let urlUpdate (result: Page option) (oldModel: Model) : Model * Cmd<ClientMsg> =
  bodyScrollEnable ()

  let model =
    { oldModel with
        Scroll =
          Map.add
            oldModel.Page
            (match oldModel.Page with
             | Page.Home -> getWindowScrollValue ()
             | Page.Contracts ->
               ContractsPageModel.getScrollValue ()
               |> Option.defaultValue 0.0
             | Page.Contract _ -> getWindowScrollValue ()
             | Page.Company -> getWindowScrollValue ()
             | Page.ContractProfitFadeReport ->
               ContractProfitFadePageModel.getScrollValue ()
               |> Option.defaultValue 0.0)
            oldModel.Scroll }

  let newModel, cmd =
    match result with
    | None
    | Some Page.Home -> { model with Page = Page.Home }, restoreScrollCmd model.Scroll Page.Home
    | Some (Page.Contract id) ->
      let contractIdParsed =
        match Int64.TryParse(id) with
        | true, parsed ->
          match model.Connection with
          | Authed -> parsed |> RSContractDetails |> Bridge.Send
          | _ -> ()

          Some parsed
        | _ -> None

      match model.Contracts, contractIdParsed with
      | Some contracts, Some parsedId ->
        if not <| Map.containsKey id contracts then
          parsedId |> RSContractInfo |> Bridge.Send
      | _ -> ()

      { model with
          Page = Page.Contract id
          ContractPageModel = { model.ContractPageModel with SelectedContractId = contractIdParsed } },
      Cmd.none
    | Some Page.Contracts ->
      ContractsPageModel.requestData model.ContractsPageModel

      { model with Page = Page.Contracts },
      Cmd.OfFunc.perform
        ContractsPageModel.restoreScrollPos
        (model.Scroll
         |> Map.tryFind Page.Contracts
         |> Option.defaultValue 0.0)
        (fun _ -> NoOp)
    | Some Page.Company ->
      CompanyPageModel.requestData model.CompanyPageModel

      { model with Page = Page.Company },
      Cmd.OfFunc.perform
        CompanyPageModel.restoreScrollPos
        (model.Scroll
         |> Map.tryFind Page.Company
         |> Option.defaultValue 0.0)
        (fun _ -> NoOp)
    | Some Page.ContractProfitFadeReport ->
      { model with Page = Page.ContractProfitFadeReport },
      Cmd.batch [
        Cmd.OfFunc.perform
          ContractProfitFadePageModel.restoreScrollPos
          (model.Scroll
           |> Map.tryFind Page.ContractProfitFadeReport
           |> Option.defaultValue 0.0)
          (fun _ -> NoOp)
        if Option.isNone model.ContractProfitFadePageModel.Contracts then
          Cmd.ofMsg (ContractProfitFadePageMsg(ContractProfitFadePageModel.Msg.ExecuteSearch Search))
      ]

  { newModel with BurgerOpen = false }, cmd

let isDemo settings =
  match settings with
  | None -> false
  | Some s -> s.IsDemo

let isSuperUser settings =
  match settings with
  | None -> false
  | Some s -> s.IsSuperUser

let init result : Model * Cmd<ClientMsg> =
  let authModel, authCmd = init ()
  let homePageModel, _homeCmd = HomePageModel.init ()
  let jobPageModel, _jobCmd = ContractPageModel.init ()
  let contractsPageModel, contractsCmd = ContractsPageModel.init ()
  let profitFadePageModel, _profitFadePageCmd = ContractProfitFadePageModel.init ()
  let companyPageModel, companyCmd = CompanyPageModel.init ()

  let initialModel =
    { Connection = Connection.Initial
      BurgerOpen = false
      HomePageModel = homePageModel
      ContractPageModel = jobPageModel
      ContractsPageModel = contractsPageModel
      ContractProfitFadePageModel = profitFadePageModel
      CompanyPageModel = companyPageModel
      Auth = authModel
      Contracts = None
      ContractAiInfos = Map.empty
      Debouncer = Debouncer.create ()
      Page = Page.Home
      Scroll = Map.empty
      Settings = None }

  let model, cmd = urlUpdate result initialModel

  model,
  Cmd.batch [
    cmd
    Cmd.map AuthMsg authCmd
    Cmd.map ContractsPageMsg contractsCmd
    Cmd.map CompanyPageMsg companyCmd
  ]

let update (msg: ClientMsg) (model: Model) : Model * Cmd<ClientMsg> =
  match msg, model.Page with
  | DebouncerSelfMsg debouncerMsg, _ ->
    let debouncerModel, debouncerCmd =
      Debouncer.update debouncerMsg model.Debouncer

    { model with Debouncer = debouncerModel }, debouncerCmd
  | ConnectionLost, _ -> { model with Connection = Disconnected }, Cmd.none
  | ToggleBurger, _ -> { model with BurgerOpen = not model.BurgerOpen }, Cmd.none
  | HomePageMsg homePageMsg, _ ->
    match homePageMsg with
    | HomePageModel.Msg.ContractClicked (contractId, tab) ->
      let debouncerModel, debouncerCmd =
        model.Debouncer
        |> Debouncer.bounce
             (TimeSpan.FromMinutes 30.0)
             $"ContractVisited_%i{contractId}"
             (HomePageMsg(HomePageModel.Msg.RemoveVisitedContract contractId))

      { model with
          ContractPageModel = { model.ContractPageModel with SelectedTab = tab }
          HomePageModel =
            { model.HomePageModel with VisitedContracts = contractId :: model.HomePageModel.VisitedContracts }
          Debouncer = debouncerModel },
      Cmd.batch [
        Cmd.map DebouncerSelfMsg debouncerCmd
        contractId
        |> string
        |> Page.Contract
        |> toPath
        |> Navigation.newUrl
      ]
    | _ ->
      let newModel, cmd =
        HomePageModel.update homePageMsg model.HomePageModel

      { model with HomePageModel = newModel }, Cmd.map HomePageMsg cmd
  | ContractPageMsg jobPageMsg, Page.Contract _ ->
    let newModel, cmd =
      ContractPageModel.update jobPageMsg model.ContractPageModel model.Contracts model.ContractAiInfos

    { model with ContractPageModel = newModel }, Cmd.map ContractPageMsg cmd
  | ContractsPageMsg contractsPageMsg, Page.Contracts ->
    let newModel, cmd =
      ContractsPageModel.update contractsPageMsg model.ContractsPageModel

    { model with ContractsPageModel = newModel }, Cmd.map ContractsPageMsg cmd
  | CompanyPageMsg contractsPageMsg, Page.Company ->
    let newModel, cmd =
      CompanyPageModel.update contractsPageMsg model.CompanyPageModel

    { model with CompanyPageModel = newModel }, Cmd.map CompanyPageMsg cmd
  | ContractProfitFadePageMsg profitFadePageMsg, Page.ContractProfitFadeReport ->
    let newModel, cmd =
      ContractProfitFadePageModel.update profitFadePageMsg model.ContractProfitFadePageModel

    { model with ContractProfitFadePageModel = newModel }, Cmd.map ContractProfitFadePageMsg cmd
  | AuthMsg authMsg, _ ->
    let authModel, cmd, externalMsg = update authMsg model.Auth

    let newModel, newCmd =
      match externalMsg with
      | ExternalMsg.NoOp -> model, Cmd.none
      | ExternalMsg.LoggedIn ->
        model,
        (if model.Connection = Connected
            || model.Connection = ReConnected then
           match authModel.User with
           | User.LoggedIn user -> Cmd.bridgeSend (RSAuth(user, Initial))
           | _ -> Cmd.none
         else
           Cmd.none)

    { newModel with Auth = authModel },
    Cmd.batch [
      Cmd.map AuthMsg cmd
      newCmd
    ]
  | RC rc, _ ->
    match rc with
    | RCBridgeConnected ->
      match model.Connection with
      | Connection.Initial ->
        match model.Auth.User with
        | User.LoggedIn user -> RSAuth(user, Initial) |> Bridge.Send
        | _ -> ()

        { model with Connection = Connected }, Cmd.none
      | Disconnected
      | ConnectionIssuesDetected
      | Authed
      | ReConnected
      | Connected ->
        match model.Auth.User with
        | User.LoggedIn user -> RSAuth(user, ReAuthentication) |> Bridge.Send
        | _ -> ()

        { model with Connection = ReConnected }, Cmd.none
    | RCNotAuthedReq -> model, Msg.LogIn |> AuthMsg |> Cmd.ofMsg
    | RCAuthed settings ->
      Bridge.Send RemoteServerMsg.RSInitializationData

      match model.ContractPageModel.SelectedContractId with
      | Some id ->
        id |> RSContractInfo |> Bridge.Send
        id |> RSContractDetails |> Bridge.Send
      | None -> ()

      match model.Page with
      | Page.Contracts -> ContractsPageModel.requestData model.ContractsPageModel
      | Page.Company -> CompanyPageModel.requestData model.CompanyPageModel
      | _ -> ()

      { model with
          Connection = Authed
          Settings = Some settings },
      match model.Page with
      | Page.ContractProfitFadeReport ->
        if Option.isNone model.ContractProfitFadePageModel.Contracts then
          Cmd.ofMsg (ContractProfitFadePageMsg(ContractProfitFadePageModel.Msg.ExecuteSearch Search))
        else
          Cmd.none
      | _ -> Cmd.none

    | RCAuthFailed -> model, Msg.LogIn |> AuthMsg |> Cmd.ofMsg
    | RCContract contract ->
      { model with
          Contracts =
            match model.Contracts with
            | None ->
              [ contract.Id.ToString(), contract ]
              |> Map.ofList
              |> Some
            | Some contracts ->
              Map.add (contract.Id.ToString()) contract contracts
              |> Some
          ContractAiInfos =
            if isDemo model.Settings then
              let aiInfo = randomAiInfo contract
              Map.add aiInfo.ContractId aiInfo model.ContractAiInfos
            else
              Map.empty },
      Cmd.none
    | RCContracts contracts ->
      { model with
          Contracts =
            contracts
            |>> fun contract -> contract.Id.ToString(), contract
            |> Map.ofList
            |> Some
          ContractAiInfos =
            if isDemo model.Settings then
              contracts
              |>> randomAiInfo
              |>> fun (info: ContractAiInfo) -> (info.ContractId, info)
              |> Map.ofList
            else
              Map.empty },
      Cmd.none
    | RCPurchaseOrders (contractId, orders) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.PurchaseOrders(contractId, orders)))
    | RCARBuckets (contractId, buckets) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.ARBuckets(contractId, buckets)))
    | RCMassPurchaseOrders orders ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.MassPurchaseOrders(orders)))
    | RCContractProfitFade (contractId, profitFade) ->
      model, Cmd.ofMsg (HomePageMsg(HomePageModel.Msg.ContractProfitFade(contractId, profitFade)))
    | RCContractProfitFades profitFades ->
      model, Cmd.ofMsg (HomePageMsg(HomePageModel.Msg.ContractProfitFades(profitFades)))
    | RCContractHomePageBuckets buckets ->
      model, Cmd.ofMsg (HomePageMsg(HomePageModel.Msg.ContractHomePageBuckets(buckets)))
    | RCChangeOrdersLoaded (contractId, orders) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.ChangeOrderHeadersLoaded(contractId, orders)))
    | RCChangeOrderItemsLoaded (orderId, items) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.ChangeOrderItemsLoaded(orderId, items)))
    | RCChangeOrderLinesLoaded (itemId, lines) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.ChangeOrderItemLinesLoaded(itemId, lines)))
    | RCJobHistories (contractId, histories) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.JobHistories(contractId, histories)))
    | RCJobForecasts (contractId, forecasts) ->
      model, Cmd.ofMsg(ContractPageMsg(ContractPageModel.Msg.JobForecasts(contractId, forecasts)))
    | RCJobSchedule (contractId, schedule) ->
      model, Cmd.ofMsg(ContractPageMsg(ContractPageModel.Msg.JobSchedule(contractId, schedule)))
    | RCJobCosts (contractId, costs) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.JobCosts({| ContractId = contractId; Jobs = costs |})))
    | RCContractProfitFadeAndProfits (contractId, fade, profitList) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.ContractProfitFade(contractId, fade, profitList)))
    | RCContractProfits (contractId, profitList) ->
      model,
      Cmd.ofMsg (
        ContractProfitFadePageMsg(ContractProfitFadePageModel.Msg.ContractProfitFades(contractId, profitList))
      )
    | RCJobCostSubs costs -> model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.JobCostSubs costs))
    | RCJobCostDetails (costId, details) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.JobCostDetails(costId, details)))
    | RCJobCostDetailsCount (costId, count) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.JobCostDetailsCount(costId, count)))
    | RCJobCostDetailsTotal (costId, field, count) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.JobCostDetailsTotal(costId, field, count)))
    | RCJobCostDetailAttachments (costId, attachments) ->
      model, Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.JobCostDetailAttachments(costId, attachments)))
    | RCJobCostDetailAttachmentContent (attachmentId, content) ->
      model,
      Cmd.ofMsg (ContractPageMsg(ContractPageModel.Msg.JobCostDetailAttachmentContent(attachmentId, content)))
    | RCSuperUserContracts contracts ->
      model, Cmd.ofMsg (ContractsPageMsg(ContractsPageModel.Msg.Contracts contracts))
    | RCContractWithProfitFades contracts ->
      model, Cmd.ofMsg (ContractProfitFadePageMsg(ContractProfitFadePageModel.Msg.Contracts contracts))
    | RCContractWithProfitFadesCount count ->
      model, Cmd.ofMsg (ContractProfitFadePageMsg(ContractProfitFadePageModel.Msg.ContractsCount(count)))
    | RCCompanyTabData data -> model, Cmd.ofMsg (CompanyPageMsg(CompanyPageModel.Msg.CompanyTabData(data)))
    | RCCompanyTabCompanies data -> model, Cmd.ofMsg (CompanyPageMsg(CompanyPageModel.Msg.Companies(data)))
    | RCNotification notification -> model, Notifications.showNotif notification
  | _, _ -> model, Cmd.none


let isPageActive model page = page = model.Page

let widerContainerClass page =
  match page with
  | Page.Contracts
  | Page.Contract _
  | Page.ContractProfitFadeReport -> "wider-container "
  | _ -> " "

let fullHeightContainer page =
  match page with
  | Page.Contracts
  | Page.ContractProfitFadeReport -> "fullheight-container "
  | _ -> " "

let footerContainer =
  Container.container [] [
    Content.content [ Content.Props [
                        Style [
                          TextAlign TextAlignOptions.Center
                        ]
                      ] ] [
      p [] [
        strong [] [ str brandedFooter ]
        $" %i{DateTime.Now.Year} created by SelectView "
        |> str
      ]
    ]
  ]

let navMenu model isBurgerOpen authModel dispatch =
  Navbar.menu [ Navbar.Menu.IsActive isBurgerOpen ] [
    Navbar.Start.div [] [
      if isSuperUser model.Settings then
        yield
          Navbar.Item.a [ Navbar.Item.Option.IsTab
                          Navbar.Item.Option.IsActive(isPageActive model Page.Contracts)
                          Navbar.Item.Props [
                            OnClick goToUrl
                            Href(toPath Page.Contracts)
                          ] ] [
            str "Contracts"
          ]

        yield
          Navbar.Item.a [ Navbar.Item.Option.IsTab
                          Navbar.Item.Option.IsActive(isPageActive model Page.ContractProfitFadeReport)
                          Navbar.Item.Props [
                            OnClick goToUrl
                            Href(toPath Page.ContractProfitFadeReport)
                          ] ] [
            str "Profit"
          ]

      if isSuperUser model.Settings
         && model.Settings
            |>> fun s -> s.ApplicationSettings.CompanyTab
            |> Option.defaultValue ApplicationSettings.Default.CompanyTab
         && model.Settings
            |>> fun settings -> settings.CanViewCompanyTab
            |> Option.defaultValue false then
        yield
          Navbar.Item.a [ Navbar.Item.Option.IsTab
                          Navbar.Item.Option.IsActive(isPageActive model Page.Company)
                          Navbar.Item.Props [
                            OnClick goToUrl
                            Href(toPath Page.Company)
                          ] ] [
            str "Company"
          ]

    ]
    Navbar.End.div [] [
      Navbar.Item.div [ Navbar.Item.HasDropdown
                        Navbar.Item.IsHoverable ] [
        Navbar.Link.div [] [
          yield
            Icon.icon [ Icon.Size ISize.IsLarge ] [
              Fa.i [ Fa.Solid.User
                     //Pulse Animation
                     //Icon 2x times larger
                     (Fa.Size Fa.FaLarge) ] []
            ]
          match authModel.User with
          | User.LoggedIn (user: UserData) -> yield str user.Username
          | _ ->
            yield
              Icon.icon [] [
                Fa.i [ Fa.Solid.Spinner
                       //Pulse Animation
                       Fa.Pulse ] []
              ]
        ]
        Navbar.Dropdown.div [] [
          Navbar.Item.a [ Navbar.Item.Props [
                            OnClick(fun _ -> Msg.LogOut |> AuthMsg |> dispatch)
                          ] ] [
            str "Logout"
          ]
        ]
      ]
    ]
  ]

let navBrand isBurgerOpen dispatch =
  Navbar.Brand.div [] [
    Navbar.Item.a [ Navbar.Item.Props [
                      Style [
                        MarginLeft "20px"
                        MarginRight "20px"
                        Padding "0"
                      ]
                    ] ] [
      img [
        Src brandedLogo
        Alt "Logo"
        Style [ MaxHeight "3.5rem" ]
      ]
    ]
    Navbar.Item.a [ Navbar.Item.Props [
                      OnClick goToUrl
                      Href(toPath Page.Home)
                    ] ] [
      str "Home"
    ]

    Navbar.burger [ Navbar.Burger.CustomClass(if isBurgerOpen then "is-active" else "")
                    Navbar.Burger.Props [
                      OnClick(fun _ -> dispatch ToggleBurger)
                    ] ] [
      span [] []
      span [] []
      span [] []
    ]
  ]

[<ReactComponent>]
let LoggedInContent (props: {| Model: Model; Dispatch: ClientMsg -> unit |}) =
  let homeDispatchCallback =
    Feliz.React.useCallback ((HomePageMsg >> props.Dispatch), [||])

  let contractPageDispatchCallback =
    Feliz.React.useCallback ((ContractPageMsg >> props.Dispatch), [||])

  let contractsPageDispatchCallback =
    Feliz.React.useCallback ((ContractsPageMsg >> props.Dispatch), [||])

  let companyPageDispatchCallback =
    Feliz.React.useCallback ((CompanyPageMsg >> props.Dispatch), [||])

  let contractProfitFadePageDispatchCallback =
    Feliz.React.useCallback ((ContractProfitFadePageMsg >> props.Dispatch), [||])

  match props.Model.Page with
  | Page.Home ->
    HomePageView.HomePageView(
      {| Model = props.Model.HomePageModel
         ApplicationSettings =
          props.Model.Settings
          |>> fun s -> s.ApplicationSettings
          |> Option.defaultValue ApplicationSettings.Default
         Contracts = props.Model.Contracts
         AiInfos = props.Model.ContractAiInfos
         IsDemo = isDemo props.Model.Settings
         IsSuperUser = isSuperUser props.Model.Settings
         Dispatch = homeDispatchCallback |}
    )
  | Page.Contract _ ->
    ContractPageView.ContractPageView(
      {| Model = props.Model.ContractPageModel
         ApplicationSettings =
          props.Model.Settings
          |>> fun s -> s.ApplicationSettings
          |> Option.defaultValue ApplicationSettings.Default
         Contracts = props.Model.Contracts
         AiInfos = props.Model.ContractAiInfos
         IsDemo = isDemo props.Model.Settings
         IsSuperUser = isSuperUser props.Model.Settings
         Dispatch = contractPageDispatchCallback |}
    )
  | Page.Contracts ->
    ContractsPageView.ContractsPageView
      {| model = props.Model.ContractsPageModel
         dispatch = contractsPageDispatchCallback |}
  | Page.Company ->
    CompanyPageView.CompanyPageView
      {| model = props.Model.CompanyPageModel
         dispatch = companyPageDispatchCallback |}
  | Page.ContractProfitFadeReport ->
    ContractProfitFadePageView.ContractProfitFadePageView(
      {| model = props.Model.ContractProfitFadePageModel
         dispatch = contractProfitFadePageDispatchCallback |}
    )

[<ReactComponent>]
let ViewComponent
  (props: {| Model: Model
             Dispatch: ClientMsg -> unit |})
  =
  let dispatch =
    Feliz.React.useCallback (props.Dispatch, [||])

  let isBurgerOpen = props.Model.BurgerOpen

  div [ Style [
          Background "#EFF3F4"
          FontFamily "'Open Sans', serif"
        ] ] [
    Navbar.navbar [] [
      Container.container [ Container.Option.CustomClass(widerContainerClass props.Model.Page) ] [
        navBrand isBurgerOpen dispatch
        navMenu props.Model isBurgerOpen props.Model.Auth dispatch
      ]
    ]
    Container.container [ Container.Option.IsFluid; Container.Option.CustomClass(
                            widerContainerClass props.Model.Page
                            + fullHeightContainer props.Model.Page) ] [
      div [ ClassName "main-container" ] [
        match props.Model.Auth.User with
        | User.LoggedIn _ ->
          yield
            LoggedInContent
              {| Model = props.Model
                 Dispatch = dispatch |}
        | User.LoggedOut ->
          yield
            div [ Style [ MarginTop "50px" ] ] [
              loadingWithMessage "Logging out..."
            ]
        | User.LoggingIn ->
          yield
            div [ Style [ MarginTop "50px" ] ] [
              loadingWithMessage "Authenticating..."
            ]
        | User.AuthException exn ->
          yield
            centeredElem (
              div [ Style [ MarginTop "50px" ] ] [
                str "Authentication exception occured:"
                br []
                $"{exn}" |> str
              ]
            )
      ]
    ]
    footer [ ClassName "footer"
             Style [ BackgroundColor "#f5f5f5" ] ] [
      footerContainer
    ]
  ]

let view model dispatch =
  ViewComponent {| Model = model; Dispatch = dispatch |}